import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useRef } from "react";

import { OrderStatusText } from "@web-app/types";

import { ORDER_STATUS_ROUTE_COLOR } from "../../DispatchMap/utils";

const Polyline = ({
  options,
  path,
}: {
  options: google.maps.PolylineOptions;
  path: { lat: number; lng: number }[];
}) => {
  const map = useMap();
  const polylineRef = useRef<google.maps.Polyline | null>(null);

  useEffect(() => {
    if (!map) return;

    if (!polylineRef.current) {
      polylineRef.current = new google.maps.Polyline({
        path,
        ...options,
      });

      polylineRef.current.setMap(map);

      return () => {
        polylineRef.current?.setMap(null);
        polylineRef.current = null;
      };
    }

    polylineRef.current.setOptions(options);
    polylineRef.current.setPath(path);
  }, [map, path, options]);

  return null;
};

const MapPolyline = ({
  polylineOptions,
  routeCoordinates,
}: {
  polylineOptions: google.maps.PolylineOptions;
  routeCoordinates: { lat: number; lng: number }[];
}) => {
  const maps = useMapsLibrary("core");
  if (!maps) {
    return null;
  }

  return (
    <Polyline
      path={routeCoordinates}
      options={{
        ...polylineOptions,
        icons: [
          {
            icon: {
              fillColor: polylineOptions.strokeColor || "#3B82F6",
              fillOpacity: 1,
              path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
              scale: 2,
              strokeColor: polylineOptions.strokeColor || "#3B82F6",
            },
            offset: "0%",
            repeat: "75px",
          },
        ],
      }}
    />
  );
};

const MapSetup = ({
  onMapReady,
}: {
  onMapReady: (map: google.maps.Map) => void;
}) => {
  const map = useMap();

  useEffect(() => {
    if (map) {
      onMapReady(map);
    }
  }, [map, onMapReady]);

  return null;
};

export type EventMarker = {
  bgColor: string;
  color: string;
  lat: number;
  lng: number;
  name: string;
  orderName?: string;
  timestamp: Date;
};

export type EventBase = {
  bgColor: string;
  color: string;
  name: string;
  orderName?: string;
  timestamp: Date | null;
};

const findClosestLocation = (
  eventTimestamp: Date,
  routePoints: { lat: number; lng: number; timestamp: string }[],
) => {
  let closestPoint = routePoints[0];
  let minTimeDiff = Math.abs(
    new Date(eventTimestamp).getTime() -
      new Date(routePoints[0].timestamp).getTime(),
  );

  routePoints.forEach((point) => {
    const timeDiff = Math.abs(
      new Date(eventTimestamp).getTime() - new Date(point.timestamp).getTime(),
    );
    if (timeDiff < minTimeDiff) {
      minTimeDiff = timeDiff;
      closestPoint = point;
    }
  });

  return { point: closestPoint, timeDiff: minTimeDiff };
};

const getRoutePositionColor = (statusText: OrderStatusText) => {
  return ORDER_STATUS_ROUTE_COLOR[statusText] || "#000000";
};

export const EVENT_COLORS = {
  ACTUAL_DROPOFF: { bgColor: "#FEE2E2", color: "#DC2626" },
  ACTUAL_PICKUP: { bgColor: "#EDE9FE", color: "#7C3AED" },
  DROPOFF_ARRIVAL: { bgColor: "#FEF3C7", color: "#D97706" },
  IN_TRANSIT: { bgColor: "#D1FAE5", color: "#059669" },
  PICKUP_ARRIVAL: { bgColor: "#DBEAFE", color: "#2563EB" },
};

export const EVENT_LEGEND = [
  { color: EVENT_COLORS.IN_TRANSIT.color, name: "In Transit" },
  { color: EVENT_COLORS.PICKUP_ARRIVAL.color, name: "Pickup Arrival Time" },
  { color: EVENT_COLORS.ACTUAL_PICKUP.color, name: "Actual Pickup Time" },
  { color: EVENT_COLORS.DROPOFF_ARRIVAL.color, name: "Drop-off Arrival Time" },
  { color: EVENT_COLORS.ACTUAL_DROPOFF.color, name: "Actual Drop-off Time" },
];

export {
  findClosestLocation,
  getRoutePositionColor,
  MapPolyline,
  MapSetup,
  Polyline,
};
